import { Box, Button, Chip, Stack, Typography } from "@mui/material";
import CalendarMonthOutlinedIcon from "@mui/icons-material/CalendarMonthOutlined";
import HomeOutlinedIcon from "@mui/icons-material/HomeOutlined";
import ChevronLeftIcon from "@mui/icons-material/ChevronLeft";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import { AppColors } from "../../constants/AppColors";
import { BookingResponse } from "../../models/ResponseModels/BookingResponse";
import { UnitResponse } from "../../models/ResponseModels/UnitResponse";
import { getBookingColor } from "../../utils/helperFunctions/bookingColorHelper";
import { useAppSelector } from "../../store/hooks";

interface MobileSingleCalendarViewProps {
  t: (key: string) => string;
  firstMonthDate: string;
  selectedUnit: UnitResponse | null;
  bookings: BookingResponse[];
  selectedStartDate: string;
  setSingleMonthDate: (value: string) => void;
  previousSingleUnit: () => void;
  nextSingleUnit: () => void;
  previousMonth: () => void;
  nextMonth: () => void;
  onDatePress: (date: string) => void;
  onBookingPress: (booking: BookingResponse) => void;
}

const weekDays = ["Mon", "Tue", "Wed", "Thu", "Fri", "Sat", "Sun"];

const toApiDate = (value?: string | null) => {
  if (!value) {
    return "";
  }

  return value.slice(0, 10);
};

const getMonthDate = (apiDate: string) => {
  const [year, month] = apiDate.split("-").map(Number);

  return new Date(year, month - 1, 1);
};

const getMonthStart = (date: Date) => {
  return `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(
    2,
    "0",
  )}-01`;
};

const getMonthTitle = (date: Date) => {
  return date.toLocaleDateString("en-GB", {
    month: "long",
    year: "numeric",
  });
};

const getMonthCalendarDays = (date: Date) => {
  const year = date.getFullYear();
  const month = date.getMonth();
  const firstDay = new Date(year, month, 1);
  const lastDay = new Date(year, month + 1, 0);
  const firstWeekDay = (firstDay.getDay() + 6) % 7;
  const days: Array<string | null> = [];

  for (let i = 0; i < firstWeekDay; i += 1) {
    days.push(null);
  }

  for (let day = 1; day <= lastDay.getDate(); day += 1) {
    days.push(
      `${year}-${String(month + 1).padStart(2, "0")}-${String(day).padStart(
        2,
        "0",
      )}`,
    );
  }

  while (days.length % 7 !== 0) {
    days.push(null);
  }

  return days;
};

const getBookingsForDate = (bookings: BookingResponse[], date: string) => {
  return bookings.filter((booking) => {
    if (booking.status === "cancelled") {
      return false;
    }

    return (
      toApiDate(booking.check_in) <= date && toApiDate(booking.check_out) > date
    );
  });
};

const getGuestName = (booking: BookingResponse, fallback: string) => {
  return booking.guest?.full_name || booking.guest?.first_name || fallback;
};

const MobileSingleCalendarView = ({
  t,
  firstMonthDate,
  selectedUnit,
  bookings,
  selectedStartDate,
  setSingleMonthDate,
  previousSingleUnit,
  nextSingleUnit,
  previousMonth,
  nextMonth,
  onDatePress,
  onBookingPress,
}: MobileSingleCalendarViewProps) => {
  const { bookingColors } = useAppSelector((state) => state.settings);
  const firstMonth = getMonthDate(firstMonthDate);
  const secondMonth = new Date(
    firstMonth.getFullYear(),
    firstMonth.getMonth() + 1,
    1,
  );
  const months = [firstMonth, secondMonth];

  return (
    <Stack spacing={2}>
      <Stack direction="row" spacing={1}>
        <Button
          variant="outlined"
          onClick={previousMonth}
          sx={{ minWidth: 48, borderRadius: "999px" }}
        >
          <ChevronLeftIcon />
        </Button>

        <Button
          variant="outlined"
          component="label"
          sx={{
            flex: 1,
            minHeight: 48,
            borderRadius: "999px",
            color: AppColors.textPrimary,
            fontWeight: 800,
            gap: 1,
            textTransform: "none",
          }}
        >
          <CalendarMonthOutlinedIcon />
          {getMonthTitle(firstMonth)}
          <input
            hidden
            type="month"
            value={firstMonthDate.slice(0, 7)}
            onChange={(event) => {
              if (event.target.value) {
                setSingleMonthDate(`${event.target.value}-01`);
              }
            }}
          />
        </Button>

        <Button
          variant="outlined"
          onClick={nextMonth}
          sx={{ minWidth: 48, borderRadius: "999px" }}
        >
          <ChevronRightIcon />
        </Button>
      </Stack>

      <Box
        sx={{
          minHeight: 58,
          bgcolor: AppColors.card,
          border: `1px solid ${AppColors.border}`,
          borderRadius: "999px",
          display: "flex",
          alignItems: "center",
          px: 1,
        }}
      >
        <Button
          onClick={previousSingleUnit}
          sx={{ minWidth: 42, width: 42, height: 42, borderRadius: "999px" }}
        >
          <ChevronLeftIcon sx={{ color: AppColors.textPrimary }} />
        </Button>

        <Box
          sx={{
            flex: 1,
            minWidth: 0,
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            gap: 1,
          }}
        >
          <HomeOutlinedIcon sx={{ color: AppColors.textPrimary }} />

          <Typography
            sx={{
              minWidth: 0,
              fontSize: 20,
              fontWeight: 800,
              color: AppColors.textPrimary,
              whiteSpace: "nowrap",
              overflow: "hidden",
              textOverflow: "ellipsis",
            }}
          >
            {selectedUnit?.name || t("calendar.noUnits")}
          </Typography>
        </Box>

        <Button
          onClick={nextSingleUnit}
          sx={{ minWidth: 42, width: 42, height: 42, borderRadius: "999px" }}
        >
          <ChevronRightIcon sx={{ color: AppColors.textPrimary }} />
        </Button>
      </Box>

      {selectedStartDate ? (
        <Box
          sx={{
            bgcolor: AppColors.primarySoft,
            borderRadius: "16px",
            p: 1.5,
          }}
        >
          <Typography
            sx={{
              fontSize: 12,
              fontWeight: 800,
              color: AppColors.primary,
            }}
          >
            {t("calendar.selectedStart")}: {selectedStartDate}.{" "}
            {t("calendar.tapCheckoutDate")}
          </Typography>
        </Box>
      ) : null}

      {months.map((monthDate) => {
        const days = getMonthCalendarDays(monthDate);

        return (
          <Box key={getMonthStart(monthDate)} sx={{ mt: 1 }}>
            <Typography sx={{ fontSize: 24, fontWeight: 900, mb: 2 }}>
              {getMonthTitle(monthDate)}
            </Typography>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(7, 1fr)",
                mb: 1,
              }}
            >
              {weekDays.map((day) => (
                <Typography
                  key={day}
                  sx={{
                    textAlign: "center",
                    fontSize: 14,
                    fontWeight: 800,
                    color:
                      day === "Sat" || day === "Sun"
                        ? AppColors.primary
                        : AppColors.textPrimary,
                  }}
                >
                  {day}
                </Typography>
              ))}
            </Box>

            <Box
              sx={{
                display: "grid",
                gridTemplateColumns: "repeat(7, minmax(0, 1fr))",
                borderLeft: `1px solid ${AppColors.border}`,
                borderTop: `1px solid ${AppColors.border}`,
                bgcolor: AppColors.card,
              }}
            >
              {days.map((date, index) => {
                if (!date) {
                  return (
                    <Box
                      key={`empty-${index}`}
                      sx={{
                        minHeight: 104,
                        bgcolor: AppColors.background,
                        borderRight: `1px solid ${AppColors.border}`,
                        borderBottom: `1px solid ${AppColors.border}`,
                      }}
                    />
                  );
                }

                const dayBookings = getBookingsForDate(bookings, date);
                const isSelectedStart = selectedStartDate === date;

                return (
                  <Box
                    key={date}
                    onClick={() => onDatePress(date)}
                    sx={{
                      minHeight: 104,
                      p: 0.75,
                      borderRight: `1px solid ${AppColors.border}`,
                      borderBottom: `1px solid ${AppColors.border}`,
                      bgcolor: isSelectedStart
                        ? AppColors.primarySoft
                        : AppColors.card,
                      cursor: selectedUnit ? "pointer" : "default",
                      overflow: "hidden",
                    }}
                  >
                    <Typography
                      sx={{
                        textAlign: "right",
                        fontSize: 14,
                        fontWeight: 800,
                        color: isSelectedStart
                          ? AppColors.primary
                          : AppColors.textPrimary,
                      }}
                    >
                      {Number(date.split("-")[2])}
                    </Typography>

                    <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                      {dayBookings.slice(0, 2).map((booking) => (
                        <Chip
                          key={booking.id}
                          size="small"
                          label={getGuestName(booking, t("booking.booking"))}
                          onClick={(event) => {
                            event.stopPropagation();
                            onBookingPress(booking);
                          }}
                          sx={{
                            height: 22,
                            maxWidth: "100%",
                            bgcolor: getBookingColor(booking, bookingColors),
                            color: AppColors.textOnPrimary,
                            fontSize: 10,
                            fontWeight: 900,
                            cursor: "pointer",
                            "& .MuiChip-label": {
                              px: 0.75,
                              overflow: "hidden",
                              textOverflow: "ellipsis",
                            },
                          }}
                        />
                      ))}
                    </Stack>

                    {dayBookings.length > 2 ? (
                      <Typography
                        sx={{
                          mt: 0.5,
                          fontSize: 10,
                          fontWeight: 900,
                          color: AppColors.textMuted,
                        }}
                      >
                        +{dayBookings.length - 2}
                      </Typography>
                    ) : null}
                  </Box>
                );
              })}
            </Box>
          </Box>
        );
      })}
    </Stack>
  );
};

export default MobileSingleCalendarView;
